import { Box, Button, Checkbox, Stack, Typography } from "@mui/material";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import { useId, useState } from "react";

/** One matching row, as much of it as this control renders. */
export interface MatchingRow {
  id: string;
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
  facilityName?: string | null;
  wasteLineNumber?: string | null;
}

export interface ResolveApplyToOthersProps {
  /** Every row carrying the same finding, whether or not the action can touch it. */
  matchingRows: readonly MatchingRow[];
  /** The subset the chosen action would actually write. Empty until an action is picked. */
  otherEligible: readonly MatchingRow[];
  /** False until the reader picks an action, which is what makes eligibility knowable. */
  actionPicked: boolean;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

function checkboxLabel(
  actionPicked: boolean,
  matchingCount: number,
  otherManifestCount: number
): string {
  if (!actionPicked) {
    const carry = matchingCount === 1 ? "row carries" : "rows carry";
    return `${matchingCount} other ${carry} this finding. Pick an action to see how many it applies to.`;
  }
  if (otherManifestCount === 0) return "No other manifest can take this action";
  const manifestWord = otherManifestCount === 1 ? "manifest" : "manifests";
  return `Also apply to ${otherManifestCount} other ${manifestWord} with this finding`;
}

function MatchingRowLine({ row, showNotEligible }: { row: MatchingRow; showNotEligible: boolean }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      sx={{
        px: 1.5,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }}>
        {manifestNumberLabel(row) || "Unknown manifest"}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
        {row.facilityName ?? "—"}
        {row.wasteLineNumber ? ` · Line ${row.wasteLineNumber}` : " · Manifest"}
      </Typography>
      {/* Named per row, because which rows an action can touch is the reason
          the count above is not simply the length of this list. */}
      {showNotEligible && (
        <Typography variant="caption" color="text.secondary">
          not eligible
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The "also apply to other manifests" checkbox, with the list of manifests it
 * would write. The count names manifests the chosen action can actually touch,
 * not every row that matched, so it never promises more than Apply delivers.
 */
export function ResolveApplyToOthers({
  matchingRows,
  otherEligible,
  actionPicked,
  checked,
  disabled,
  onChange,
}: ResolveApplyToOthersProps) {
  const [showMatchingList, setShowMatchingList] = useState(false);
  const checkboxId = useId();
  const otherManifestCount = new Set(otherEligible.map((r) => r.processedManifestId)).size;

  return (
    /* One flex row: the checkbox, then a column holding everything that
       describes it. The label, the toggle and the list share that column's
       left edge, so they line up without any of them carrying an offset that
       has to match the checkbox's width. */
    <Box sx={{ mt: 1.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Checkbox
        id={checkboxId}
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ p: 0, mt: 0.25 }}
      />
      <Stack sx={{ flexGrow: 1, minWidth: 0, alignItems: "flex-start" }}>
        <Typography
          component="label"
          htmlFor={checkboxId}
          variant="body2"
          sx={{ cursor: disabled ? "default" : "pointer" }}
        >
          {checkboxLabel(actionPicked, matchingRows.length, otherManifestCount)}
        </Typography>
        {/* Inline rather than a popover: the list is what the reader checks
            before ticking the box, and a popover here would open over the note
            field they fill in next. No horizontal padding, so the button's
            text starts at the column edge rather than inset from it. */}
        <Button
          size="small"
          onClick={() => setShowMatchingList((v) => !v)}
          sx={{ px: 0, minWidth: 0, textTransform: "none" }}
        >
          {showMatchingList ? "Hide the manifests" : "Show the manifests"}
        </Button>
        {showMatchingList && (
          <Box
            sx={{
              alignSelf: "stretch",
              mt: 0.5,
              maxHeight: 180,
              overflowY: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {matchingRows.map((row) => (
              <MatchingRowLine
                key={row.id}
                row={row}
                showNotEligible={actionPicked && !otherEligible.some((r) => r.id === row.id)}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
